import { useMemo, useRef, useState } from 'react';
import Button from '@/components/base/Button';
import type { BulkCandidate, BulkChannel } from '@/types/portal';

interface ManualEntryPanelProps {
  candidates: BulkCandidate[];
  /** Hired creators who aren't payable yet: listed with the reason, can't be picked. */
  waiting: BulkCandidate[];
  channel: BulkChannel;
  currency: string;
  description: string;
  onDescription: (value: string) => void;
  uniformAmount: number;
  onUniformAmount: (value: number) => void;
  onApplyUniform: () => void;
  onAdd: (candidate: BulkCandidate) => void;
}

function targetText(candidate: BulkCandidate, channel: BulkChannel): string {
  if (channel === 'mobile') return `${candidate.network ?? 'Mobile Money'} · ${candidate.walletId}`;
  return `${candidate.walletType} wallet · ${candidate.walletId}`;
}

export default function ManualEntryPanel({
  candidates,
  waiting,
  channel,
  currency,
  description,
  onDescription,
  uniformAmount,
  onUniformAmount,
  onApplyUniform,
  onAdd,
}: ManualEntryPanelProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [campaign, setCampaign] = useState('');
  const blurTimer = useRef<number | null>(null);

  const matches = useMemo(() => {
    const text = query.trim().toLowerCase();
    // Payable creators first, then the ones who can't be paid yet (shown greyed with the reason).
    // With a campaign picked, the list is that campaign's creators, all of them.
    const everyone = [...candidates, ...waiting].filter(
      (candidate) => !campaign || candidate.campaign === campaign,
    );
    if (!text) return campaign ? everyone : everyone.slice(0, 6);
    return everyone
      .filter(
        (candidate) =>
          candidate.name.toLowerCase().includes(text) ||
          candidate.walletId.toLowerCase().includes(text) ||
          candidate.campaign.toLowerCase().includes(text),
      )
      .slice(0, campaign ? undefined : 8);
  }, [candidates, waiting, query, campaign]);

  const campaignNames = useMemo(
    () =>
      Array.from(
        new Set([...candidates, ...waiting].map((candidate) => candidate.campaign)),
      ).sort(),
    [candidates, waiting],
  );

  const selected = candidates.find((candidate) => candidate.id === selectedId) ?? null;

  const add = () => {
    if (!selected) return;
    onAdd(selected);
    setSelectedId(null);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label
            htmlFor="uniform-amount"
            className="text-xs font-label font-medium text-foreground-600"
          >
            Apply uniform amount
          </label>
          <div className="mt-1.5 flex">
            <span className="flex h-10 items-center rounded-l-md border border-r-0 border-background-300 bg-background-100 px-3 text-xs font-label font-medium text-foreground-600">
              {currency || '—'}
            </span>
            <input
              id="uniform-amount"
              type="number"
              min={0}
              value={uniformAmount || ''}
              onChange={(event) => onUniformAmount(Number(event.target.value))}
              placeholder="e.g. 5000"
              className="h-10 min-w-0 flex-1 border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <button
              type="button"
              onClick={onApplyUniform}
              className="h-10 rounded-r-md border border-background-300 bg-background-100 px-3 text-xs font-label font-medium text-foreground-800 cursor-pointer transition-colors hover:bg-background-200"
            >
              Apply
            </button>
          </div>
        </div>

        <div>
          <label
            htmlFor="default-description"
            className="text-xs font-label font-medium text-foreground-600"
          >
            Default description
          </label>
          <input
            id="default-description"
            value={description}
            onChange={(event) => onDescription(event.target.value)}
            maxLength={80}
            className="mt-1.5 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label
          htmlFor="add-recipient"
          className="text-xs font-label font-medium text-foreground-600"
        >
          Add Recipient
        </label>
        <div className="mt-1.5 flex flex-col gap-2 sm:flex-row">
          <select
            aria-label="Filter by campaign"
            value={campaign}
            onChange={(event) => {
              setCampaign(event.target.value);
              setSelectedId(null);
              setQuery('');
              setOpen(Boolean(event.target.value));
            }}
            className="h-10 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 cursor-pointer focus:border-primary-400 focus:outline-none sm:w-56"
          >
            <option value="">All campaigns</option>
            {campaignNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <div className="flex min-w-0 flex-1 gap-2">
            <div className="relative min-w-0 flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-base text-foreground-400">
                <i className="ri-search-line" />
              </span>
              <input
                id="add-recipient"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setSelectedId(null);
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onBlur={() => {
                  blurTimer.current = window.setTimeout(() => setOpen(false), 150);
                }}
                placeholder="Search by name, wallet ID or campaign..."
                className="h-10 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-9 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
              <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400" />

              {open && matches.length > 0 ? (
                <ul className="absolute left-0 right-0 z-30 mt-1 max-h-64 overflow-y-auto rounded-md border border-background-300 bg-background-50 py-1">
                  {matches.map((candidate) => (
                    <li key={candidate.id}>
                      <button
                        type="button"
                        disabled={Boolean(candidate.waiting)}
                        title={
                          candidate.waiting ? `Can't be paid yet: ${candidate.waiting}` : undefined
                        }
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          setSelectedId(candidate.id);
                          setQuery(candidate.name);
                          setOpen(false);
                          if (blurTimer.current) window.clearTimeout(blurTimer.current);
                        }}
                        className="flex w-full cursor-pointer items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-foreground-900">
                            {candidate.name}
                          </span>
                          <span className="block truncate text-xs text-foreground-500">
                            {candidate.waiting ? (
                              <>
                                <i className="ri-time-line mr-1 text-secondary-600" />
                                {candidate.waiting} · {candidate.campaign}
                              </>
                            ) : (
                              targetText(candidate, channel)
                            )}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs text-foreground-400">
                          {candidate.market}
                        </span>
                      </button>
                    </li>
                  ))}
                  {matches.some((candidate) => candidate.waiting) ? (
                    <li className="mt-1 flex items-start gap-1.5 border-t border-background-200 px-3 pb-1 pt-2 text-xs text-foreground-500">
                      <i className="ri-time-line mt-px text-secondary-600" />
                      Greyed out: hired but not payable yet, so they can&apos;t be added. The reason
                      is under each name.
                    </li>
                  ) : null}
                </ul>
              ) : null}
            </div>
            <Button icon="ri-add-line" onClick={add} disabled={!selected}>
              Add
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
